/**
 * Team: je verantwortlicher Person die Zahlen des Zeitraums.
 *
 * „Ohne Zuordnung" ist eine eigene Zeile und kein stilles Wegfallen — Befund 7
 * des Audits: die Projekte eines ausgeschiedenen Koordinators verschwanden
 * vorher aus jeder Kachel.
 */
import React from 'react';
import { Activity } from 'lucide-react';
import type { CockpitModel, EmployeeRow } from '../../services/cockpitTypes';
import { Bar, Panel, Trend } from './CockpitBits';
import { CAP_MINUTES, MO, fmtCents, pctOf } from './cockpitUi';

const TH = 'px-3 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase';
const TD = 'px-3 py-3 text-xs';

const hasValues = (e: EmployeeRow): boolean =>
    e.projectCount > 0 || e.performedCents !== 0 || e.forecastCents !== 0 || e.inspections > 0;

/**
 * Leistung im Zeitraum — unabhaengig davon, ob die Person als Koordinator
 * gefuehrt wird. Ohne diese Zeile fiel jemand aus der Tabelle, dessen Projekte
 * inzwischen alle beendet sind: die Rolle steht dann auf Innendienst, die
 * erbrachte Leistung des Zeitraums aber bleibt.
 */
const hasPerformance = (e: EmployeeRow): boolean =>
    e.performedCents !== 0 || e.performedSelfCents !== 0 || e.inspections > 0;

export interface TeamTabProps {
    model: CockpitModel;
    hideAmounts: boolean;
    todayMonth: number | null;
    /** Der gewaehlte Verantwortliche der Werkzeugleiste, `all` = ungefiltert. */
    employeeId: string;
    onSelectEmployee: (id: string) => void;
}

export const TeamTab: React.FC<TeamTabProps> = ({ model, hideAmounts, todayMonth, employeeId, onSelectEmployee }) => {
    const lastMonth = model.periodMonths.length > 0 ? model.periodMonths[model.periodMonths.length - 1] : 11;
    const utilMonth = todayMonth ?? lastMonth;

    // Mit gesetztem Mitarbeiterfilter sind alle anderen Zeilen null — dann
    // bleibt stehen, was noch Zahlen hat, und in jedem Fall die gewaehlte Person.
    const visible = (e: EmployeeRow): boolean => (employeeId !== 'all'
        ? e.id === employeeId || hasPerformance(e)
        : e.isFieldCoordinator || hasPerformance(e));

    const field = model.employees
        .filter((e) => e.id !== 'unassigned' && visible(e))
        .sort((a, b) => b.performedCents - a.performedCents);
    const unassigned = model.employees.find((e) => e.id === 'unassigned');
    const inTable = new Set(field.map((e) => e.id));
    const backoffice = model.employees.filter(
        (e) => e.id !== 'unassigned' && !e.isFieldCoordinator && !inTable.has(e.id),
    );

    const rows = unassigned && hasValues(unassigned) ? [...field, unassigned] : field;

    return (
        <div data-glass-view="cockpit" className="space-y-4">
            <Panel
                title={`Mitarbeiter (${rows.length})`}
                icon={<Activity className="w-4 h-4 text-blue-500" />}
                bodyClassName="overflow-x-auto"
            >
                <table className="w-full text-left">
                    <thead>
                        <tr className="border-b border-slate-100">
                            <th className={`${TH} pl-5`}>Mitarbeiter</th>
                            <th className={TH}>Projekte</th>
                            <th className={TH}>Erbracht</th>
                            <th className={TH}>davon selbst ausgeführt</th>
                            <th className={TH}>Forecast</th>
                            <th className={TH}>Realisierung</th>
                            <th className={TH}>Begehungen</th>
                            <th className={TH}>Ø je Begehung</th>
                            <th className={TH}>Trend</th>
                            <th className={TH}>Auslastung ({MO[utilMonth]})</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {rows.map((e) => {
                            const realisation = pctOf(e.performedCents, e.forecastCents);
                            // Auslastung = max(Ist, Plan) des Monats (Engine), wie im Reiter Auslastung.
                            const util = Math.round(((e.workloadMinutes[utilMonth] ?? 0) / CAP_MINUTES) * 100);
                            const utilIstH = Math.round((e.workloadIstMinutes[utilMonth] ?? 0) / 60);
                            const utilPlanH = Math.round((e.workloadPlanMinutes[utilMonth] ?? 0) / 60);
                            const isUnassigned = e.id === 'unassigned';
                            return (
                                <tr
                                    key={e.id}
                                    className={`hover:bg-slate-50 group ${isUnassigned ? 'bg-amber-50/40' : 'cursor-pointer'}`}
                                    onClick={() => { if (!isUnassigned) onSelectEmployee(e.id); }}
                                >
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold shrink-0 ${isUnassigned ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500 group-hover:bg-blue-500 group-hover:text-white'}`}>
                                                {isUnassigned ? '?' : e.name.charAt(0)}
                                            </div>
                                            <span className={`text-sm font-semibold ${isUnassigned ? 'text-amber-700' : 'text-slate-800 group-hover:text-blue-600'}`}>
                                                {isUnassigned ? 'Ohne Zuordnung' : e.name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className={`${TD} text-slate-600`}>{e.projectCount}</td>
                                    <td className={`${TD} font-semibold text-emerald-700`}>{fmtCents(e.performedCents, hideAmounts)}</td>
                                    <td className={`${TD} text-slate-600`}>{fmtCents(e.performedSelfCents, hideAmounts)}</td>
                                    <td className={`${TD} text-slate-500`}>{fmtCents(e.forecastCents, hideAmounts)}</td>
                                    <td className={TD}>
                                        <div className="flex items-center gap-1">
                                            <div className="w-10">
                                                <Bar value={realisation} max={100} height="h-1" color={realisation > 80 ? 'bg-emerald-500' : realisation > 50 ? 'bg-amber-400' : 'bg-red-400'} />
                                            </div>
                                            <span className="text-[11px] font-semibold text-slate-600">{realisation}%</span>
                                        </div>
                                    </td>
                                    <td className={`${TD} text-slate-600`}>{e.inspections}</td>
                                    <td className={`${TD} text-indigo-600`}>
                                        {e.avgPerInspectionCents === null ? '—' : fmtCents(e.avgPerInspectionCents, hideAmounts)}
                                    </td>
                                    <td className={TD}>
                                        <Trend current={e.monthlyPerformed[lastMonth] ?? 0} previous={e.prevMonthPerformedCents} />
                                    </td>
                                    <td className={TD}>
                                        <div className="flex items-center gap-1.5" title={`Ist ${utilIstH} h · Plan ${utilPlanH} h — es gilt der größere Wert`}>
                                            <div className="w-16 bg-slate-100 h-2 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${util > 90 ? 'bg-red-500' : util > 75 ? 'bg-orange-500' : 'bg-emerald-500'}`}
                                                    style={{ width: `${Math.min(100, util)}%` }}
                                                />
                                            </div>
                                            <span className="text-[11px] font-semibold text-slate-600">{util}%</span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {/* Die beiden Spalten zaehlen NICHT dasselbe — ohne diesen Satz
                    liest man die Differenz als Fehler. */}
                <div className="px-5 py-3 text-[11px] text-slate-400">
                    Selbst ausgeführt zählt auch Leistung auf Projekten anderer Verantwortlicher;
                    Erbracht zählt nur die eigenen Projekte.
                </div>
            </Panel>

            {backoffice.length > 0 && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                    <div className="text-[11px] font-semibold text-slate-400 uppercase mb-2">Innendienst / Verwaltung</div>
                    <div className="flex flex-wrap gap-4">
                        {backoffice.map((e) => (
                            <div key={e.id} className="flex items-center gap-2 text-xs text-slate-500">
                                <div className="w-6 h-6 rounded bg-slate-200 flex items-center justify-center text-[11px] font-semibold text-slate-500">
                                    {e.name.charAt(0)}
                                </div>
                                <span>{e.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
